<script setup>
import { inject } from 'vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import SettingsSection from '@/components/settings/SettingsSection.vue';
import { SCHOOL_SETTINGS_KEY } from '@/composables/useSchoolSettingsForm';

// Who signs Form 29 (Teacher's Individual Teaching Load), one block per
// signature in print order, each person typed in as a name and position.
// "Prepared by" and "Checked by" depend on the teacher's level, so they
// have a Junior High and a Senior High person side by side.
const GROUPS = [
  {
    title: 'Prepared by',
    hint: "Prints the person for the teacher's level.",
    people: [
      { key: 'form29PreparedByJhs', caption: 'Junior High', position: 'e.g. Grade 7 - Curriculum Level Head' },
      { key: 'form29PreparedByShs', caption: 'Senior High', position: 'e.g. Grade 11 - Curriculum Level Head' },
    ],
  },
  {
    title: 'Checked by',
    hint: "Prints the person for the teacher's level.",
    people: [
      { key: 'form29CheckedByJhs', caption: 'Junior High', position: 'e.g. JHS Academic Head' },
      { key: 'form29CheckedByShs', caption: 'Senior High', position: 'e.g. SHS Academic Head' },
    ],
  },
  {
    title: 'Reviewed by',
    hint: 'Both print, side by side.',
    people: [
      { key: 'form29ReviewedBy1', caption: 'First', position: 'e.g. Principal II / Assistant to the Office of the Principal' },
      { key: 'form29ReviewedBy2', caption: 'Second', position: 'e.g. Principal IV' },
    ],
  },
  {
    title: 'Recommending Approval',
    people: [{ key: 'form29Recommending', position: 'e.g. Education Program Supervisor' }],
  },
  {
    title: 'Approved by',
    people: [{ key: 'form29ApprovedBy', position: 'e.g. Chief, Curriculum Implementation Division' }],
  },
];
const PEOPLE = GROUPS.flatMap((g) => g.people);

// The DepEd Personnel picks some of these replaced are saved as cleared, so
// an old pick can't print behind an empty name.
const IDS = ['form29CheckedByJhsID', 'form29CheckedByShsID', 'form29RecommendingID', 'form29ApprovedByID'];
const KEYS = [...IDS, ...PEOPLE.flatMap(({ key }) => [`${key}Name`, `${key}Position`])];
const { form, saving, savedAt, isDirty, reset, save } = inject(SCHOOL_SETTINGS_KEY);

function saveSignatories() {
  for (const id of IDS) form[id] = null;
  save('form29', KEYS);
}
</script>

<template>
  <SettingsSection
    title="Form 29 signatories"
    description="Who signs Form 29 (Teacher's Individual Teaching Load). Leave a name blank to print an empty signature line."
    :dirty="isDirty(KEYS)"
    :saving="saving === 'form29'"
    :saved-at="savedAt.form29 || 0"
    @save="saveSignatories"
    @reset="reset(KEYS)"
  >
    <div class="signatories">
      <fieldset v-for="group in GROUPS" :key="group.title" class="signatory-group">
        <legend class="signatory-group__title">{{ group.title }}</legend>
        <p v-if="group.hint" class="signatory-group__hint">{{ group.hint }}</p>

        <!-- Two people: one column each. One person: name and position side by side. -->
        <div class="form-grid">
          <template v-if="group.people.length > 1">
            <div v-for="p in group.people" :key="p.key" class="signatory">
              <span class="signatory__caption">{{ p.caption }}</span>
              <UiFormField label="Name" v-slot="{ id }">
                <v-text-field :id="id" v-model="form[`${p.key}Name`]" placeholder="Full name" maxlength="150" hide-details="auto" />
              </UiFormField>
              <UiFormField label="Position" v-slot="{ id }">
                <v-text-field :id="id" v-model="form[`${p.key}Position`]" :placeholder="p.position" maxlength="150" hide-details="auto" />
              </UiFormField>
            </div>
          </template>
          <template v-else>
            <UiFormField label="Name" v-slot="{ id }">
              <v-text-field :id="id" v-model="form[`${group.people[0].key}Name`]" placeholder="Full name" maxlength="150" hide-details="auto" />
            </UiFormField>
            <UiFormField label="Position" v-slot="{ id }">
              <v-text-field
                :id="id"
                v-model="form[`${group.people[0].key}Position`]"
                :placeholder="group.people[0].position"
                maxlength="150"
                hide-details="auto"
              />
            </UiFormField>
          </template>
        </div>
      </fieldset>
    </div>
  </SettingsSection>
</template>

<style scoped>
.signatories {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.signatory-group {
  min-width: 0;
  margin: 0;
  padding: 0 0 20px;
  border: 0;
  border-bottom: 1px solid var(--border);
}
.signatory-group:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}
.signatory-group__title {
  padding: 0;
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 600;
  color: var(--foreground);
}
.signatory-group__hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--muted-foreground);
}
.signatory-group__title + .form-grid {
  margin-top: 12px;
}
.signatory {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}
.signatory__caption {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}
</style>
